import type { CVRCompanyDetails } from '../types'
import {
  companyCountryFlagCode,
  companyStatusBadgeTone,
  formatCompanyForm,
  formatStatusLabel,
} from './format'

/** How a profile is listed under »Senest åbnet« on the home page. */
export interface VisitLabel {
  name: string
  countryCode?: string | null
  detail?: string | null
}

/**
 * »Reel ejer: Anna Ejer +1« when CVR names beneficial owners, otherwise
 * form and town. A status other than normal goes first.
 */
export function companyVisitLabel(company: CVRCompanyDetails): VisitLabel {
  const parts: string[] = []
  if (companyStatusBadgeTone(company.status) !== 'active') {
    parts.push(formatStatusLabel(company.status))
  }
  const owners = (company.ownership?.beneficialOwners ?? []).filter((p) => p.role.isActive)
  if (owners.length > 0) {
    parts.push(`Reel ejer: ${owners[0].navn}${owners.length > 1 ? ` +${owners.length - 1}` : ''}`)
  } else {
    const form = formatCompanyForm(company.virksomhedsform)
    if (form) parts.push(form)
    if (company.postdistrikt) parts.push(company.postdistrikt)
  }
  return {
    name: company.navn || `CVR ${company.cvrNummer}`,
    countryCode: companyCountryFlagCode(company),
    detail: parts.join(' · ') || null,
  }
}

/** »Person · 3 aktive selskaber«. */
export function personVisitLabel(
  name: string,
  countryCode: string | null,
  activeCompanies: number
): VisitLabel {
  const roles =
    activeCompanies === 0
      ? 'ingen aktive roller'
      : activeCompanies === 1
        ? '1 aktivt selskab'
        : `${activeCompanies} aktive selskaber`
  return { name, countryCode, detail: `Person · ${roles}` }
}

/** »i dag«, »i går«, otherwise »3. okt.« (with the year when it is not this year). */
export function visitedLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(now) - day(d)) / 86_400_000)
  if (diff <= 0) return 'i dag'
  if (diff === 1) return 'i går'
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  })
}
